import styled from 'styled-components';
import { CustomBadge } from '@ardoq/status-ui';
import { IconSize, InfoIcon } from '@ardoq/icons';
import { Text } from '@ardoq/typography';
import { colors, s8 } from '@ardoq/design-tokens';
import {
  PlainTextPopover,
  PopoverContentType,
  WithPopoverPlacement,
  WithPopover,
} from '@ardoq/popovers';
import { Stack } from '@ardoq/layout';
import { TrendResultStatus } from '@ardoq/api-types';
import { EmptyTrendUIProps, TrendUIProps } from './types';
import { type I18nLocale, t, type WithI18nLocale } from '@ardoq/i18n';

const TrendContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${s8};
  flex-shrink: 0;
`;

const getTrendPopoverContent = (
  props: TrendUIProps,
  i18nLocale: I18nLocale
): PopoverContentType =>
  !props.isExactTrend || props.percentage === 0
    ? () => {
        const helperText = !props.isExactTrend
          ? props.daysAgo === 1
            ? t(
                i18nLocale,
                'dashboard:trend.popoverPartialDataSingular',
                { daysAgo: props.daysAgo, period: props.period },
                'Data is only available for the last {{daysAgo}} day. As more data becomes available, the full {{period}}-day period will be reflected.'
              )
            : t(
                i18nLocale,
                'dashboard:trend.popoverPartialDataPlural',
                { daysAgo: props.daysAgo, period: props.period },
                'Data is only available for the last {{daysAgo}} days. As more data becomes available, the full {{period}}-day period will be reflected.'
              )
          : t(
              i18nLocale,
              'dashboard:trend.popoverPercentageZero',
              'Percentage change is not available because the previous value was 0.'
            );
        return (
          <PlainTextPopover>
            <Stack>
              <Text variant={'text2Bold'} color={'textModerate'}>
                {props.tooltipText}
              </Text>
              <Text variant={'text2'}>{helperText}</Text>
            </Stack>
          </PlainTextPopover>
        );
      }
    : undefined;

export const TrendUI = (props: TrendUIProps & WithI18nLocale) => {
  const { i18nLocale } = props;
  const periodWithValue =
    (!props.isExactTrend && props.daysAgo) || props.period;
  return (
    <TrendContainer>
      <CustomBadge
        statusType={props.tagStatusType}
        label={
          props.isUnchanged
            ? t(i18nLocale, 'dashboard:trend.unchanged', 'Unchanged')
            : props.percentage === 0
              ? 'N/A'
              : `${props.percentage} %`
        }
        iconName={props.iconName}
      />
      <Text color={'textModerate'} variant={'text2'}>
        {periodWithValue === 1
          ? t(
              i18nLocale,
              'dashboard:trend.vsDaysAgoSingular',
              { periodWithValue },
              'vs {{periodWithValue}} day ago'
            )
          : t(
              i18nLocale,
              'dashboard:trend.vsDaysAgoPlural',
              { periodWithValue },
              'vs {{periodWithValue}} days ago'
            )}
      </Text>
      <WithPopover
        preferredPlacement={WithPopoverPlacement.TOP}
        content={getTrendPopoverContent(props, i18nLocale)}
      >
        <InfoIcon
          data-tooltip-text={
            !props.isExactTrend ? undefined : props.tooltipText
          }
          iconSize={IconSize.SMALL}
          color={colors.icon}
        />
      </WithPopover>
    </TrendContainer>
  );
};

export const EmptyTrend = (props: EmptyTrendUIProps & WithI18nLocale) => {
  const { i18nLocale } = props;
  return (
    <TrendContainer>
      <Text color={'textSubtle'} variant={'text2'}>
        {props.status === TrendResultStatus.MISSING
          ? t(
              i18nLocale,
              'dashboard:trend.noTrendAvailable',
              'No trend available yet'
            )
          : t(i18nLocale, 'dashboard:trend.unavailable', 'Unavailable')}
      </Text>
      <WithPopover
        preferredPlacement={WithPopoverPlacement.TOP}
        content={() => (
          <PlainTextPopover>
            {props.status === TrendResultStatus.MISSING
              ? t(
                  i18nLocale,
                  'dashboard:trend.missingDataPopover',
                  { period: props.period },
                  'No data is available yet, but it will be reflected as it becomes available in {{period}} days.'
                )
              : t(
                  i18nLocale,
                  'dashboard:trend.filteredPopover',
                  'Trend is unavailable when dashboard filters are applied.'
                )}
          </PlainTextPopover>
        )}
      >
        <InfoIcon iconSize={IconSize.SMALL} color={colors.icon} />
      </WithPopover>
    </TrendContainer>
  );
};
